import type { CircleAiConversation } from "@circle-react/api/circleAiConversationsApi";
import { circleAiConversationsApi } from "@circle-react/api/circleAiConversationsApi";
import { CIRCLE_AI_CONVERSATIONS_QUERY_KEY } from "@circle-react/components/CircleAi/Home/RecentChatsDrawer/useRecentChatsList";
import { useCircleAiStore } from "@circle-react/components/CircleAi/useCircleAiStore";
import { useCallback, useState } from "react";
import { useMutation, useQueryClient } from "react-query";

import { useConversationActionsToast } from "./useConversationActionsToast";

type RenamableConversation = Pick<CircleAiConversation, "uuid" | "title">;

interface UseRenameConversationOptions {
  errorMessage: string;
  successMessage: string;
}

export const useRenameConversation = ({
  errorMessage,
  successMessage,
}: UseRenameConversationOptions) => {
  const queryClient = useQueryClient();
  const { success, error } = useConversationActionsToast();
  const [conversationPendingRename, setConversationPendingRename] =
    useState<RenamableConversation | null>(null);

  const { mutate, isPending } = useMutation({
    mutationFn: ({ uuid, title }: { uuid: string; title: string }) =>
      circleAiConversationsApi.update({ id: uuid, title }),
    onSuccess: (_data, { uuid, title }) => {
      void queryClient.invalidateQueries({
        queryKey: [CIRCLE_AI_CONVERSATIONS_QUERY_KEY],
      });
      const store = useCircleAiStore.getState();
      if (uuid === store.activeConversation.id) {
        store.setActiveConversation({ ...store.activeConversation, title });
      }
      success(successMessage);
    },
    onError: () => {
      error(errorMessage);
    },
  });

  const requestRename = useCallback(
    (conversation: RenamableConversation) =>
      setConversationPendingRename({
        uuid: conversation.uuid,
        title: conversation.title,
      }),
    [],
  );

  const cancelRename = useCallback(() => {
    if (isPending) return;
    setConversationPendingRename(null);
  }, [isPending]);

  const confirmRename = useCallback(
    (title: string) => {
      if (!conversationPendingRename) return;

      mutate(
        { uuid: conversationPendingRename.uuid, title },
        { onSuccess: () => setConversationPendingRename(null) },
      );
    },
    [conversationPendingRename, mutate],
  );

  return {
    conversationPendingRename,
    isRenaming: isPending,
    requestRename,
    cancelRename,
    confirmRename,
  };
};
